import type { OpenCodeApi } from "./opencodeApi"
import type { Session } from "./opencodeTypes"

// A workspace session has exactly one chat.
//
// The container is the unit of isolation, and inside it there is one
// conversation with the agent — not a list of them. This resolves which of the
// agent's sessions that conversation is.

/**
 * The session's chat: the oldest root session, or undefined if there is none.
 *
 * Two rules, both load-bearing.
 *
 * **Roots only.** The agent creates sessions of its own — a child session per
 * sub-agent or task it delegates to, marked with `parentID`. Those come back
 * from `GET /api/session` alongside the real one, and this surface has no
 * `roots` filter to do it server-side. Skipping this filter means the chat
 * silently switches to a sub-agent's transcript the first time the agent
 * delegates.
 *
 * **Oldest, not newest.** Stability is the point: the oldest root does not move
 * as the agent creates and finishes children, so a reload lands on the same
 * conversation rather than wherever the list happens to start.
 */
export function pickChatSession(sessions: Session[]): Session | undefined {
  return sessions
    .filter((session) => !session.parentID)
    .sort((a, b) => Date.parse(a.time.created) - Date.parse(b.time.created))[0]
}

/**
 * The session's chat, created on first use.
 *
 * Lazy rather than created with the container: a container that is never opened
 * needs no conversation, and doing it here keeps a failing agent from failing
 * the container build.
 */
export async function ensureChatSession(api: OpenCodeApi): Promise<Session> {
  const existing = pickChatSession(await api.listSessions())
  if (existing) return existing
  return api.createSession()
}
